import { useEffect } from "react";
import InnerHTML from "dangerously-set-html-content";
import { useFormContext, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useIsSitesEnabled } from "@/react/components/SettingsApp/Site/Pages/useIsSitesEnabled";
import { settingsNavPathV3 } from "@/react/helpers/urlHelpers";
import { useMobileWebFlags } from "@circle-react/hooks/useMobileWebFlags";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Form } from "@circle-react-uikit/Form";
import { OptionDescription } from "./OptionDescription";
import { usePublishedPages } from "./usePublishedPages";

const i18nRoot = "spaces.form.edit.customize";

const options = (isSitesEnabled: boolean) => [
  {
    label: t("spaces.form.create.visibility_types.open"),
    value: "open",
    description: (
      <OptionDescription
        accessDesc={t("customize_space.access_form.open_access")}
        visibilityDesc={t("customize_space.access_form.open_visibility")}
        isAccessOpen
      />
    ),
  },
  {
    label: t("spaces.form.create.visibility_types.private"),
    value: "private",
    description: (
      <OptionDescription
        accessDesc={t("customize_space.access_form.private_access")}
        visibilityDesc={t("customize_space.access_form.private_visibility")}
        lockScreenDesc={
          isSitesEnabled
            ? t("customize_space.access_form.private_lock_screen")
            : undefined
        }
      />
    ),
  },
  {
    label: t("spaces.form.create.visibility_types.secret"),
    value: "secret",
    description: (
      <OptionDescription
        accessDesc={t("customize_space.access_form.secret_access")}
        visibilityDesc={t("customize_space.access_form.secret_visibility")}
      />
    ),
  },
];

interface AccessFormProps {
  onChange?: (hasChanges: boolean) => void;
  onExit?: () => void;
}

export const AccessForm = ({ onChange, onExit }: AccessFormProps) => {
  const {
    formState: { dirtyFields },
  } = useFormContext();

  const visibility = useWatch({ name: "visibility" });
  const isPrivateAccess = visibility === "private";
  const { isMobileWebNewSpaceCustomizationUIEnabled } = useMobileWebFlags();
  const isSitesEnabled = useIsSitesEnabled();
  const { pages, isLoading: isLoadingPages } = usePublishedPages({
    shouldFilterRestrictedPages: true,
  });

  useEffect(() => {
    if (
      "visibility" in dirtyFields ||
      "lockscreen_site_page_id" in dirtyFields
    ) {
      onChange?.(true);
    }
  }, [dirtyFields.visibility, dirtyFields.lockscreen_site_page_id]);

  return (
    <>
      <Form.Item
        name="visibility"
        hideDescription
        rules={{
          required: t("spaces.form.create.choose_visibility"),
          maxLength: 255,
        }}
        hideBorder
        hideLabel
        fullWidth
        variant="extra-bottom-margin"
      >
        <Form.RadioGroup
          variant={isMobileWebNewSpaceCustomizationUIEnabled ? "box" : "radio"}
          name="visibility"
          options={options(isSitesEnabled)}
          optionAlignment="left"
        />
      </Form.Item>
      {isPrivateAccess && isSitesEnabled && (
        <Form.Item
          hideBorder
          hideDescription
          label={t([i18nRoot, "lockscreen_site_page.label"])}
          name="lockscreen_site_page_id"
          variant="extra-bottom-margin"
        >
          <div className="mb-3">
            <Typography.BodySm color="text-default">
              <InnerHTML
                className="mr-1 inline"
                html={t([i18nRoot, "lockscreen_site_page.description"])}
              />
              <Link
                to={settingsNavPathV3.content.pages}
                onClick={onExit}
                className="text-default hover:text-dark"
              >
                <u>{t([i18nRoot, "lockscreen_site_page.manage_all_pages"])}</u>
              </Link>
            </Typography.BodySm>
          </div>
          {!isLoadingPages && (
            <Form.SelectNative
              name="lockscreen_site_page_id"
              options={pages.map(page => ({
                label: page.name,
                value: page.id,
              }))}
              className="w-full rounded-lg py-3 pl-4 pr-8"
              isPromptDisabled={false}
              prompt={t([
                i18nRoot,
                "lockscreen_site_page",
                pages.length === 0 ? "empty_prompt" : "prompt",
              ])}
            />
          )}
        </Form.Item>
      )}
      {isPrivateAccess && (
        <Form.Item
          inlineReverse
          hideBorder
          hideDescription
          label={t([i18nRoot, "show_lock_icon_to_non_members"])}
          tooltipText={t([
            i18nRoot,
            "show_lock_icon_to_non_members_description",
          ])}
          name="show_lock_icon_for_non_members"
          shouldToggleValueOnLabelClick
        >
          <Form.ToggleSwitch
            name="show_lock_icon_for_non_members"
            variant="small"
          />
        </Form.Item>
      )}
    </>
  );
};
